/**
 * Auth Hook
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores';
import {
  initializeLiff,
  isLineLoggedIn,
  lineLogin,
  lineLogout,
  getLineProfile,
} from '../api/auth/line-auth';
import { checkBinding, bindEmployee, getCurrentUser } from '../api/services/auth.service';
import { setLineUserId, clearAuth } from '../api/client';
import type { BindEmployeeRequest } from '../types';

export function useAuth() {
  const queryClient = useQueryClient();
  const {
    status,
    employee,
    userProfile,
    lineProfile,
    permissions,
    setAuthenticated,
    setNotBound,
    logout: storeLogout,
    hasPermission,
    setStatus,
  } = useAuthStore();

  // Initialize auth
  const initialize = useCallback(
    async (liffId: string) => {
      try {
        setStatus('loading');

        // Initialize LIFF
        console.log('[Auth] Initializing LIFF...');
        const initialized = await initializeLiff(liffId);
        if (!initialized) {
          console.log('[Auth] LIFF initialization failed');
          setStatus('unauthenticated');
          return;
        }

        // Check if logged in via LINE
        if (!isLineLoggedIn()) {
          console.log('[Auth] Not logged in via LINE');
          setStatus('unauthenticated');
          return;
        }

        // Get LINE profile
        console.log('[Auth] Getting LINE profile...');
        const profile = await getLineProfile();
        if (!profile) {
          console.log('[Auth] Failed to get LINE profile');
          setStatus('unauthenticated');
          return;
        }

        console.log('[Auth] LINE profile:', profile.displayName);

        // Set LINE user ID for API calls
        setLineUserId(profile.userId);

        // Check binding
        console.log('[Auth] Checking binding...');
        const bindingResult = await checkBinding(profile.userId);
        if (!bindingResult.success || !bindingResult.data?.is_bound) {
          console.log('[Auth] Not bound, redirecting to bind page');
          setNotBound(profile);
          return;
        }

        // Get user profile
        console.log('[Auth] Getting user profile...');
        const userResult = await getCurrentUser();
        if (!userResult.success || !userResult.data) {
          console.log('[Auth] Failed to get user profile');
          setNotBound(profile);
          return;
        }

        console.log('[Auth] Authenticated:', userResult.data.employee?.name);
        setAuthenticated(userResult.data, profile);
      } catch (error) {
        console.error('[Auth] Initialization error:', error);
        setStatus('unauthenticated');
      }
    },
    [setStatus, setNotBound, setAuthenticated]
  );

  // Login
  const login = useCallback((redirectUri?: string) => {
    lineLogin(redirectUri);
  }, []);

  // Logout
  const logout = useCallback(() => {
    lineLogout();
    clearAuth();
    storeLogout();
    queryClient.clear();
  }, [storeLogout, queryClient]);

  // Bind employee mutation
  const bindMutation = useMutation({
    mutationFn: async (data: Omit<BindEmployeeRequest, 'line_user_id'>) => {
      const profile = await getLineProfile();
      if (!profile) {
        throw new Error('Unable to get LINE profile');
      }

      const result = await bindEmployee({
        ...data,
        line_user_id: profile.userId,
        line_display_name: profile.displayName,
        line_picture_url: profile.pictureUrl,
      });

      if (!result.success) {
        throw new Error(result.error?.message || 'Binding failed');
      }

      if (!result.data?.matched) {
        throw new Error(result.data?.message || 'Employee not found');
      }

      return result.data;
    },
    onSuccess: async () => {
      // Refresh user profile
      const profile = await getLineProfile();
      if (profile) {
        const userResult = await getCurrentUser();
        if (userResult.success && userResult.data) {
          setAuthenticated(userResult.data, profile);
        }
      }
    },
  });

  // Refresh user profile
  const refreshProfile = useCallback(async () => {
    const profile = await getLineProfile();
    if (!profile) return;

    const userResult = await getCurrentUser();
    if (userResult.success && userResult.data) {
      setAuthenticated(userResult.data, profile);
    }
  }, [setAuthenticated]);

  return {
    // State
    status,
    isLoading: status === 'loading',
    isAuthenticated: status === 'authenticated',
    isNotBound: status === 'not_bound',
    employee,
    userProfile,
    lineProfile,
    permissions,

    // Actions
    initialize,
    login,
    logout,
    bind: bindMutation.mutate,
    bindAsync: bindMutation.mutateAsync,
    isBinding: bindMutation.isPending,
    bindError: bindMutation.error,
    refreshProfile,
    hasPermission,
  };
}

// Query hook for current user
export function useCurrentUser() {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const result = await getCurrentUser();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to get user');
      }
      return result.data;
    },
    enabled: useAuthStore.getState().status === 'authenticated',
  });
}
